import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { usePickerSearch, PickerSearchHeader, PickerNoMatches } from "@/lib/picker-search";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import {
  Laugh, Package, Sparkles, Loader2, Check, AlertTriangle, RefreshCw, Download, Bookmark,
  X, Trash2, ImageIcon, Search, Pencil, Crop,
} from "lucide-react";
import { type AspectRatio, AD_ASPECT_RATIO_OPTIONS } from "@shared/aspectRatios";
import { loadLastAspectRatios, saveLastAspectRatios } from "@/lib/aspectRatio";
import type { Product, Offer, MemeRun, MemeRunResult } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { InpaintingModal } from "@/components/campaign-wizard/inpainting-modal";
import { SORT_TABS, rankOf, type SortTabKey } from "@/components/meme-settings";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type ImgflipTemplate = {
  id: string;
  name: string;
  url: string;
  width: number;
  height: number;
  boxCount: number;
  // Imgflip sort ranks (1-based; null = not currently in that sort). Absent
  // entirely on the unseeded live-scrape fallback.
  rankTopNew?: number | null;
  rankTop30?: number | null;
  rankTopAll?: number | null;
  // Membership in the curated, global "Recommended" list (same for all users).
  recommended?: boolean;
};

// A template with no rank in any Imgflip sort — i.e. imported on demand via
// Search Imgflip (or dropped out of all sorts). Shown under the Imported tab.
function isImportedTemplate(t: ImgflipTemplate): boolean {
  return (t.rankTopNew ?? null) === null && (t.rankTop30 ?? null) === null && (t.rankTopAll ?? null) === null;
}

// One Imgflip search result, annotated server-side with its library standing:
//   importable — not in the library yet (show Import)
//   analyzing  — enrichment in flight (poll)
//   available  — enriched, selectable for runs
//   pending    — in the library un-enriched with no failure (Import re-kicks)
//   failed     — last enrichment failed / image dead (Import retries)
type ImgflipSearchResult = ImgflipTemplate & {
  status: "importable" | "analyzing" | "available" | "failed" | "pending";
  enrichError: string | null;
};


type MemeRunWithResults = MemeRun & { results: MemeRunResult[] };

const ACTIVE_RUN_STATUSES = new Set(["generating", "compositing"]);

function runStatusMeta(status: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  switch (status) {
    case "generating": return { label: "Rendering memes", variant: "secondary" };
    case "compositing": return { label: "Rendering memes", variant: "secondary" };
    case "ready": return { label: "Ready", variant: "default" };
    case "failed": return { label: "Failed", variant: "destructive" };
    default: return { label: status, variant: "outline" };
  }
}

// First usable image URL for a product/offer. Products prefer their gallery
// (mediaJson); both fall back to the AI reference images (adgenImageRefs).
function firstImageUrl(entity: { mediaJson?: unknown; adgenImageRefs?: unknown } | undefined | null): string | null {
  if (!entity) return null;
  for (const pool of [entity.mediaJson, entity.adgenImageRefs]) {
    if (Array.isArray(pool)) {
      const found = pool.find((u): u is string => typeof u === "string" && u.trim().length > 0);
      if (found) return found;
    }
  }
  return null;
}

type PickerOption = { value: string; label: string; imageUrl?: string | null; description?: string | null };
type PickerGroup = { label: string; options: PickerOption[] };

// Product/Offer picker — matches the Slideshow Express / UGC Express look.
function PickerSelect({
  icon, label, placeholder, value, onChange, options, groups, testId, disabled, searchable,
}: {
  icon: ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options?: PickerOption[];
  groups?: PickerGroup[];
  testId: string;
  disabled?: boolean;
  searchable?: boolean;
}) {
  const filled = !!value && value !== "__none__";
  const allOptions = useMemo<PickerOption[]>(
    () => (groups ? groups.flatMap((g) => g.options) : (options ?? [])),
    [groups, options],
  );
  const hasAvatars = allOptions.some((o) => !!o.imageUrl);
  const selected = filled ? allOptions.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  const [open, setOpen] = useState(false);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, allOptions.length, !!searchable);
  const matchesQuery = (o: PickerOption) => matchesLabel(o.label);
  const filteredOptions = isSearching ? (options ?? []).filter(matchesQuery) : (options ?? []);
  const filteredGroups = (groups ?? [])
    .map((g) => ({ label: g.label, options: isSearching ? g.options.filter(matchesQuery) : g.options }))
    .filter((g) => g.options.length > 0);
  const hasAnyResult = groups ? filteredGroups.length > 0 : filteredOptions.length > 0;
  const renderOption = (o: PickerOption) => {
    const desc = o.description?.trim() || "";
    return (
      <SelectItem key={o.value} value={o.value} className="text-xs">
        <div className="flex items-center gap-2 min-w-0">
          {hasAvatars ? (
            o.imageUrl ? (
              <img src={thumbUrl(o.imageUrl, 64)!} onError={thumbFallback(o.imageUrl)} alt="" className="h-6 w-6 rounded-md object-cover shrink-0 border border-border/40" loading="lazy" />
            ) : (
              <div className="h-6 w-6 rounded-md bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                {o.label.trim().charAt(0).toUpperCase() || "?"}
              </div>
            )
          ) : null}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="truncate">{o.label}</span>
            {desc && (
              <span className="text-[11px] text-muted-foreground/80 leading-snug mt-0.5 truncate" data-testid={`text-option-description-${o.value}`}>
                {desc}
              </span>
            )}
          </div>
        </div>
      </SelectItem>
    );
  };
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled} open={open} onOpenChange={setOpen}>
      <SelectTrigger
        className={[
          "group h-auto min-h-[72px] rounded-xl px-3 py-2.5 transition-all duration-150",
          "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          filled
            ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
        ].join(" ")}
        data-testid={testId}
      >
        <div className="flex items-center gap-3 w-full min-w-0">
          <div
            className={[
              "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
              filled
                ? "bg-primary/15 text-primary ring-primary/20"
                : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
            ].join(" ")}
          >
            {icon}
          </div>
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
              {label}
            </span>
            {filled && selected ? (
              <div className="flex flex-col w-full min-w-0">
                <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                  {selected.label}
                </span>
                {selectedDescription && (
                  <span className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5">
                    {selectedDescription}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                <SelectValue placeholder={placeholder} />
              </span>
            )}
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
        {showSearch && (
          <PickerSearchHeader inputRef={searchInputRef} query={query} onQueryChange={setQuery} testId={`input-search-${testId}`} />
        )}
        {showSearch && !hasAnyResult && <PickerNoMatches />}
        {groups
          ? filteredGroups.map((g) => (
              <SelectGroup key={g.label}>
                <SelectLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">{g.label}</SelectLabel>
                {g.options.map(renderOption)}
              </SelectGroup>
            ))
          : filteredOptions.map(renderOption)}
      </SelectContent>
    </Select>
  );
}

// ── Result card ──────────────────────────────────────────────────────────────

function ResultCard({ brandId, result }: { brandId: string; result: MemeRunResult }) {
  const { toast } = useToast();
  const isReady = result.status === "ready" && !!result.compositedImageUrl;
  const isFailed = result.status === "failed";
  const isPending = !isReady && !isFailed;
  const imageUrl = result.compositedImageUrl || result.rawImageUrl || result.templateUrl;
  const isSaved = !!result.savedGeneratedAdId;
  const isEditing = result.editStatus === "editing";
  const editFailed = result.editStatus === "failed";
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const startEdit = useMutation({
    mutationFn: (instructions: string) =>
      apiRequest("POST", `/api/brands/${brandId}/meme-runs/${result.runId}/results/${result.id}/edit`, {
        instructions,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/meme-runs`] });
      toast({ title: "Edit started", description: "The meme is being re-rendered with your changes." });
    },
  });

  const saveToLibrary = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/brands/${brandId}/meme-runs/${result.runId}/results/${result.id}/save`),
    onSuccess: () => {
      toast({ title: "Saved to My Ads", description: `"${result.templateName}" was added to your library.` });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/meme-runs`] });
      // Invalidate every filter variation of the library listing so My Ads
      // shows the meme immediately, mirroring the Ads Express save flow.
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0] as string).startsWith(`/api/brands/${brandId}/generated-ads`),
      });
    },
    onError: (e: any) =>
      toast({ title: "Save failed", description: e?.message, variant: "destructive" }),
  });

  const deleteResult = useMutation({
    mutationFn: () =>
      apiRequest("DELETE", `/api/brands/${brandId}/meme-runs/${result.runId}/results/${result.id}`),
    onSuccess: () => {
      // Remove only this tile immediately, keeping the run mounted even if it
      // now has zero results. The invalidate reconciles any concurrent worker
      // changes without falling back to a different run.
      queryClient.setQueryData<MemeRunWithResults[]>(
        [`/api/brands/${brandId}/meme-runs`],
        (runs) => runs?.map((run) =>
          run.id === result.runId
            ? { ...run, results: run.results.filter((item) => item.id !== result.id) }
            : run,
        ),
      );
      setDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/meme-runs`] });
      toast({ title: "Meme deleted", description: `"${result.templateName}" was removed from this run.` });
    },
    onError: (e: any) =>
      toast({ title: "Could not delete meme", description: e?.message, variant: "destructive" }),
  });

  const canDelete = isReady || isFailed;

  return (
    <div className="rounded-xl border border-border/60 bg-background/60 overflow-hidden flex flex-col" data-testid={`card-meme-result-${result.id}`}>
      <div className="relative bg-muted/40 flex items-center justify-center min-h-[180px]">
        {isPending ? (
          <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs">Rendering this meme…</span>
          </div>
        ) : isFailed ? (
          <div className="flex flex-col items-center gap-2 py-10 px-4 text-center text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <span className="text-xs break-words whitespace-pre-wrap max-w-full" data-testid={`text-result-error-${result.id}`}>{result.error || "Failed"}</span>
          </div>
        ) : (
          <>
            <img src={imageUrl} alt={result.templateName} className="w-full object-contain max-h-[360px]" loading="lazy" data-testid={`img-meme-result-${result.id}`} />
            {isEditing && (
              <div className="absolute inset-0 bg-background/70 flex flex-col items-center justify-center gap-2 text-muted-foreground" data-testid={`overlay-editing-${result.id}`}>
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-xs">Applying your edit…</span>
              </div>
            )}
          </>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="text-xs font-semibold truncate" data-testid={`text-result-template-${result.id}`}>{result.templateName}</span>
          <div className="flex items-center gap-1 shrink-0">
            {/* Ratio indicator (mirrors Ads Express) so variants are tellable
                apart when multiple ratios were selected. Absent on legacy
                results without a stored ratio. */}
            {result.aspectRatio && (
              <Badge variant="outline" className="shrink-0 text-[10px]" data-testid={`badge-result-ratio-${result.id}`}>
                {result.aspectRatio}
              </Badge>
            )}
            {isReady && <Badge variant="default" className="shrink-0 text-[10px]">Ready</Badge>}
          </div>
        </div>
        {editFailed && result.editError && (
          <p className="text-xs text-destructive break-words" data-testid={`text-edit-error-${result.id}`}>
            Edit failed: {result.editError}
          </p>
        )}
        {isReady && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={isSaved ? "secondary" : "default"}
              className="h-7 text-xs flex-1"
              onClick={() => saveToLibrary.mutate()}
              disabled={isSaved || saveToLibrary.isPending}
              data-testid={`button-save-${result.id}`}
            >
              {saveToLibrary.isPending ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…</>
              ) : isSaved ? (
                <><Check className="h-3 w-3 mr-1" /> Saved</>
              ) : (
                <><Bookmark className="h-3 w-3 mr-1" /> Save to My Ads</>
              )}
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => setEditOpen(true)}
              disabled={isEditing}
              data-testid={`button-edit-${result.id}`}
            >
              {isEditing ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Editing…</>
              ) : (
                <><Pencil className="h-3 w-3 mr-1" /> Edit</>
              )}
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs flex-1" asChild
              data-testid={`button-download-${result.id}`}
            >
              <a href={imageUrl} target="_blank" rel="noreferrer" download>
                <Download className="h-3 w-3 mr-1" /> Download
              </a>
            </Button>
          </div>
        )}
        {canDelete && (
          <div className="flex justify-end pt-1">
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                  disabled={isEditing || deleteResult.isPending}
                  data-testid={`button-delete-result-${result.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this meme?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes "{result.templateName}" from this run. Any meme already saved to My Ads will stay in your library.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleteResult.isPending}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(event) => {
                      event.preventDefault();
                      deleteResult.mutate();
                    }}
                    disabled={deleteResult.isPending}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid={`button-confirm-delete-result-${result.id}`}
                  >
                    {deleteResult.isPending ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Deleting…</>
                    ) : (
                      "Delete meme"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Task #1484 — meme edits open the shared Ads Express edit modal in
          layout-only mode: same look (large image + instruction box), but the
          submit delegates to the existing meme edit endpoint. Region tools,
          SAM click-to-select, and reference uploads never appear for memes. */}
        <InpaintingModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        imageUrl={imageUrl}
        onAccept={() => {}}
        title="Edit this meme"
        description="Describe the change you want — e.g. new caption text, something to add or remove. The meme keeps the same template."
        simpleSubmit={async (instructions) => {
          await startEdit.mutateAsync(instructions);
        }}
      />
    </div>
  );
}

// ── Run card ─────────────────────────────────────────────────────────────────

function RunCard({ brandId, run, entityName, onDeleted }: { brandId: string; run: MemeRunWithResults; entityName: string; onDeleted?: () => void }) {
  const { toast } = useToast();
  const meta = runStatusMeta(run.status);
  const isActive = ACTIVE_RUN_STATUSES.has(run.status);

  const retryRun = useMutation({
    mutationFn: () => apiRequest("POST", `/api/brands/${brandId}/meme-runs/${run.id}/retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/meme-runs`] });
      toast({ title: "Retrying", description: "The run is being regenerated." });
    },
    onError: (e: any) => toast({ title: "Could not retry", description: e?.message, variant: "destructive" }),
  });

  const deleteRun = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/brands/${brandId}/meme-runs/${run.id}`),
    onSuccess: () => {
      // Clear the page's session pin on this run so the view falls back to
      // the next most recent run instead of waiting on a deleted id.
      onDeleted?.();
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/meme-runs`] });
      toast({ title: "Deleted", description: "The run and its memes were removed." });
    },
    onError: (e: any) => toast({ title: "Could not delete", description: e?.message, variant: "destructive" }),
  });

  return (
    <Card data-testid={`card-meme-run-${run.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold truncate" data-testid={`text-run-name-${run.id}`}>{run.name || "Meme run"}</span>
            {entityName && <Badge variant="outline" className="shrink-0 text-[10px]">{entityName}</Badge>}
            <Badge variant={meta.variant} className="shrink-0 text-[10px]" data-testid={`status-run-${run.id}`}>
              {isActive && <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />}
              {meta.label}
            </Badge>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {run.status === "failed" && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => retryRun.mutate()} disabled={retryRun.isPending} data-testid={`button-retry-run-${run.id}`}>
                <RefreshCw className={cn("h-3 w-3 mr-1", retryRun.isPending && "animate-spin")} /> Retry
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deleteRun.mutate()} disabled={deleteRun.isPending} data-testid={`button-delete-run-${run.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {run.status === "failed" && run.error && (
          <p className="text-xs text-destructive" data-testid={`text-run-error-${run.id}`}>{run.error}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {run.results.map((r) => (
            <ResultCard key={r.id} brandId={brandId} result={r} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ── Page ─────────────────────────────────────────────────────────────────────

export default function MemeGeneratorPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedOfferId, setSelectedOfferId] = useState<string>("");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  // Output aspect ratios — same shared multi-select pattern (and localStorage
  // memory) as Ads Express: restores the last selection, defaults to a single
  // 4:5 on first use. One meme is generated per (template × ratio).
  const [aspectRatios, setAspectRatios] = useState<AspectRatio[]>(() => loadLastAspectRatios());
  const [templateSearch, setTemplateSearch] = useState("");
  // Active-run model (mirrors Ads Express): the page shows ONE run — the one
  // just started this session, falling back to the most recent run on load —
  // instead of stacking the entire run history. Older runs stay in the DB and
  // their saved memes remain in My Ads; this is display-only.
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const isOfferMode = promotedItemType === "offer";

  const { data: templatesData, isLoading: templatesLoading, isError: templatesError } = useQuery<{ templates: ImgflipTemplate[]; unseeded?: boolean }>({
    queryKey: ["/api/meme-generator/templates"],
    staleTime: 10 * 60_000,
  });
  const templates = templatesData?.templates ?? [];
  const unseeded = !!templatesData?.unseeded;

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers = [] } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });

  const { data: runs = [], isLoading: runsLoading } = useQuery<MemeRunWithResults[]>({
    queryKey: [`/api/brands/${brandId}/meme-runs`],
    enabled: !!brandId,
    refetchInterval: (query) => {
      const rows = (query.state.data as MemeRunWithResults[] | undefined) ?? [];
      // Poll only while the DISPLAYED run is still generating (the run picked
      // this session, else the latest). Runs are returned newest-first.
      const shown = (activeRunId ? rows.find((r) => r.id === activeRunId) : undefined) ?? rows[0];
      if (!shown) return false;
      // Also poll while any of the shown run's memes has an in-flight edit
      // (the edit worker flips editStatus back to null/failed when done).
      const anyEditing = shown.results?.some((r) => r.editStatus === "editing");
      return ACTIVE_RUN_STATUSES.has(shown.status) || anyEditing ? 4000 : false;
    },
  });

  // The single run shown on the page: the run started this session if it still
  // exists, else the most recent run. Deleting the shown run clears the
  // session pin so the view falls back to the next latest run — but while a
  // just-created run hasn't landed in the refetched list yet, we show a
  // loading placeholder instead of flashing the PREVIOUS run.
  const sessionRun = activeRunId ? runs.find((r) => r.id === activeRunId) : undefined;
  const awaitingNewRun = !!activeRunId && !sessionRun;
  const activeRun = sessionRun ?? (awaitingNewRun ? undefined : runs[0]);

  const createRun = useMutation({
    mutationFn: async () => {
      // Only IDs are sent — the server resolves names/URLs from the trusted
      // Imgflip catalog itself.
      const res = await apiRequest("POST", `/api/brands/${brandId}/meme-runs`, {
        productId: isOfferMode ? undefined : selectedProductId || undefined,
        offerId: isOfferMode ? selectedOfferId || undefined : undefined,
        templates: selectedTemplateIds,
        aspectRatios,
      });
      // Remember the selection for next visit (same helper Ads Express uses).
      saveLastAspectRatios(aspectRatios);
      return res.json();
    },
    onSuccess: (data: { run?: { id?: string } }) => {
      // Pivot the view to the new run immediately — previous runs' memes no
      // longer linger on screen (they stay in the DB / My Ads).
      if (data?.run?.id) setActiveRunId(data.run.id);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/meme-runs`] });
      setSelectedTemplateIds([]);
      toast({ title: "Generating memes", description: "One meme per selected template and aspect ratio is being created." });
    },
    onError: (e: any) => {
      toast({ title: "Could not start", description: e?.message || "Failed to create meme run", variant: "destructive" });
    },
  });

  const toggleTemplate = (id: string) => {
    setSelectedTemplateIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  // Picker tab: the three Imgflip sorts, plus Imported (rank-less templates
  // brought in via Search Imgflip) and the Search Imgflip tab itself. Hidden
  // on the unseeded live-scrape fallback, which only ever has top-new
  // templates.
  type PickerTab = SortTabKey | "recommended" | "imported" | "search";
  const [pickerSort, setPickerSort] = useState<PickerTab>("top-new");
  const isSearchTab = pickerSort === "search";
  // The curated Recommended list is global (same for everyone). Its tab only
  // appears when the curator has picked at least one template.
  const hasRecommended = useMemo(() => !unseeded && templates.some((t) => t.recommended), [templates, unseeded]);
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    let rows = templates;
    if (!unseeded && pickerSort === "recommended") {
      rows = rows.filter((t) => !!t.recommended);
    } else if (!unseeded && pickerSort === "imported") {
      rows = rows.filter(isImportedTemplate);
    } else if (!unseeded && pickerSort !== "search") {
      const rankKey = SORT_TABS.find((s) => s.key === pickerSort)!.rankKey;
      const inSort = rows
        .filter((t) => rankOf(t, rankKey) !== null)
        .sort((a, b) => (rankOf(a, rankKey)! - rankOf(b, rankKey)!));
      // Older libraries (imported before sort tracking) may have no ranks at
      // all — fall back to the full list rather than an empty grid.
      if (inSort.length) rows = inSort;
    }
    if (!q) return rows;
    return rows.filter((t) => t.name.toLowerCase().includes(q));
  }, [templates, templateSearch, unseeded, pickerSort]);

  // ── Search Imgflip (full public catalog) ──────────────────────────────────
  // The same search box drives this tab, debounced against Imgflip. Imports
  // send only the template id + search context — the server re-resolves the
  // template against Imgflip itself.
  const [imgflipQuery, setImgflipQuery] = useState("");
  const [imgflipPage, setImgflipPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => {
      setImgflipQuery(templateSearch.trim());
      setImgflipPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [templateSearch]);
  const searchEnabled = isSearchTab && imgflipQuery.length >= 2;
  const searchUrl = `/api/meme-generator/imgflip-search?q=${encodeURIComponent(imgflipQuery)}&page=${imgflipPage}`;
  const {
    data: searchData,
    isLoading: searchLoading,
    isError: searchIsError,
    isFetching: searchFetching,
  } = useQuery<{ results: ImgflipSearchResult[]; hasMore: boolean; page: number }>({
    queryKey: [searchUrl],
    enabled: searchEnabled,
    staleTime: 0,
    // Poll while any visible result is being analyzed so "Analyzing…" flips
    // to "Ready" without a manual refresh.
    refetchInterval: (query) => {
      const d = query.state.data as { results?: ImgflipSearchResult[] } | undefined;
      return d?.results?.some((r) => r.status === "analyzing") ? 3000 : false;
    },
  });
  const searchResults = searchData?.results ?? [];

  // When a template finishes analyzing, refresh the library-backed lists so
  // it appears under Imported (and in Meme Settings) immediately.
  const prevAnalyzingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const nowAnalyzing = new Set(searchResults.filter((r) => r.status === "analyzing").map((r) => r.id));
    const anyFinished = [...prevAnalyzingRef.current].some((id) => {
      const r = searchResults.find((x) => x.id === id);
      return !!r && r.status !== "analyzing";
    });
    if (anyFinished) {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
    }
    prevAnalyzingRef.current = nowAnalyzing;
  }, [searchResults]);

  // Local optimistic "importing" set so a clicked tile shows Analyzing…
  // immediately (several imports can be in flight at once).
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());
  const importTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      const res = await apiRequest("POST", "/api/meme-generator/template-library/import", {
        templateId,
        query: imgflipQuery,
        page: imgflipPage,
      });
      return res.json();
    },
    onMutate: (templateId: string) => {
      setImportingIds((prev) => new Set(prev).add(templateId));
    },
    onSettled: (_data, _err, templateId: string) => {
      // Keep the optimistic flag until the next search refetch lands (the
      // server-side in-flight set takes over from there).
      queryClient.invalidateQueries({ queryKey: [searchUrl] }).finally(() => {
        setImportingIds((prev) => {
          const next = new Set(prev);
          next.delete(templateId);
          return next;
        });
      });
    },
    onError: (e: any) => {
      toast({ title: "Import failed", description: e?.message || "Could not import the template", variant: "destructive" });
    },
  });

  const entityNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of products) map.set(p.id, p.name);
    for (const o of offers) map.set(o.id, o.name);
    return map;
  }, [products, offers]);

  // Total memes this run will produce: one per (template × ratio). The server
  // caps a run at 20 memes, so the fan-out is surfaced (and gated) here too.
  const totalMemes = selectedTemplateIds.length * Math.max(aspectRatios.length, 1);
  const overMemeCap = totalMemes > 20;

  const canGenerate =
    selectedTemplateIds.length > 0 &&
    aspectRatios.length > 0 &&
    !overMemeCap &&
    (isOfferMode ? !!selectedOfferId : !!selectedProductId) &&
    !createRun.isPending;

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-8 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Laugh className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">Meme Generator</h1>
          <p className="text-sm text-muted-foreground">
            Pick a product or offer and one or more trending meme templates. Each template becomes the reference image for an AI render that reimagines it around your product — finished memes appear below.
          </p>
        </div>
      </div>

      <div className="space-y-5">
      {/* ── Inputs: product/offer + template multi-select ── */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-2 md:max-w-md">
              <div className="flex items-center gap-1 rounded-lg bg-muted/60 p-1 w-fit">
                <button
                  type="button"
                  className={cn("rounded-md px-3 py-1 text-xs font-medium transition-colors", !isOfferMode ? "bg-background shadow-sm" : "text-muted-foreground")}
                  onClick={() => setPromotedItemType("product")}
                  data-testid="button-mode-product"
                >
                  Product
                </button>
                <button
                  type="button"
                  className={cn("rounded-md px-3 py-1 text-xs font-medium transition-colors", isOfferMode ? "bg-background shadow-sm" : "text-muted-foreground")}
                  onClick={() => setPromotedItemType("offer")}
                  data-testid="button-mode-offer"
                >
                  Offer
                </button>
              </div>
              {isOfferMode ? (
                <PickerSelect
                  icon={<Sparkles className="h-5 w-5" />}
                  label="Offer"
                  placeholder="Select an offer to promote"
                  value={selectedOfferId}
                  onChange={setSelectedOfferId}
                  options={offers.map((o) => ({ value: o.id, label: o.name, imageUrl: firstImageUrl(o as any) }))}
                  testId="select-offer"
                  searchable
                />
              ) : (
                <PickerSelect
                  icon={<Package className="h-5 w-5" />}
                  label="Product"
                  placeholder="Select a product to promote"
                  value={selectedProductId}
                  onChange={setSelectedProductId}
                  options={products.map((p) => ({ value: p.id, label: p.name, imageUrl: firstImageUrl(p as any) }))}
                  testId="select-product"
                  searchable
                />
              )}
            </div>
          </div>

          {/* Template grid */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold">Meme templates</span>
                {!unseeded && (
                  <div className="flex items-center gap-1 rounded-lg bg-muted/60 p-1 w-fit" data-testid="tabs-template-sort">
                    {[
                      ...(hasRecommended ? [{ key: "recommended" as const, label: "Recommended" }] : []),
                      ...SORT_TABS,
                      { key: "imported" as const, label: "Imported" },
                      { key: "search" as const, label: "Search Imgflip" },
                    ].map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        className={cn(
                          "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                          pickerSort === s.key ? "bg-background shadow-sm" : "text-muted-foreground",
                        )}
                        onClick={() => setPickerSort(s.key)}
                        data-testid={`button-sort-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  placeholder={isSearchTab ? "Search all of Imgflip…" : "Search templates…"}
                  className="h-8 pl-8 text-xs"
                  data-testid="input-template-search"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {unseeded
                ? "The current top image meme templates (Imgflip, live). Click to select."
                : isSearchTab
                  ? "Search Imgflip's full public catalog. Import a template to analyze it — once ready, it's selectable here and lives under the Imported tab."
                  : pickerSort === "imported"
                    ? "Templates imported on demand via Search Imgflip. They keep their stored context and survive the daily template refresh. Click to select."
                    : "Approved templates from the meme library — each carries stored context about how the meme works, which is sent to the AI. Click to select."}
            </p>
            {isSearchTab && !unseeded ? (
              <div className="space-y-3">
                {!searchEnabled ? (
                  <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="text-search-hint">
                    <Search className="h-4 w-4 shrink-0" /> Type at least 2 characters to search Imgflip's full template catalog.
                  </div>
                ) : searchLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <Skeleton key={i} className="aspect-square rounded-xl" />
                    ))}
                  </div>
                ) : searchIsError ? (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive flex items-center gap-2" data-testid="text-search-error">
                    <AlertTriangle className="h-4 w-4 shrink-0" /> Imgflip search failed. Try again shortly.
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="text-search-empty">
                    <ImageIcon className="h-4 w-4 shrink-0" /> No Imgflip templates match "{imgflipQuery}".
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[480px] overflow-y-auto pr-1">
                    {searchResults.map((r) => {
                      const analyzing = r.status === "analyzing" || importingIds.has(r.id);
                      const available = r.status === "available" && !analyzing;
                      const selected = available && selectedTemplateIds.includes(r.id);
                      const failed = r.status === "failed" && !analyzing;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          disabled={analyzing}
                          onClick={() => {
                            if (available) toggleTemplate(r.id);
                            else if (!analyzing) importTemplate.mutate(r.id);
                          }}
                          className={cn(
                            "group relative rounded-xl border overflow-hidden text-left transition-all",
                            selected
                              ? "border-primary ring-2 ring-primary/40"
                              : "border-border/60 hover:border-primary/40 hover:shadow-sm",
                            analyzing && "opacity-80 cursor-default",
                          )}
                          data-testid={`card-search-result-${r.id}`}
                          title={failed ? r.enrichError || "Analysis failed — click to retry" : undefined}
                        >
                          <div className="aspect-square bg-muted/40">
                            <img src={r.url} alt={r.name} className="h-full w-full object-cover" loading="lazy" />
                          </div>
                          {selected && (
                            <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow">
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                          <div className="absolute top-1.5 left-1.5" data-testid={`status-search-result-${r.id}`}>
                            {analyzing ? (
                              <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0">
                                <Loader2 className="h-2.5 w-2.5 animate-spin" /> Analyzing…
                              </Badge>
                            ) : available ? (
                              <Badge className="text-[10px] px-1.5 py-0">Ready</Badge>
                            ) : failed ? (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Failed — retry</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0 bg-background/80">
                                <Download className="h-2.5 w-2.5" /> Import
                              </Badge>
                            )}
                          </div>
                          <div className="p-1.5">
                            <span className="text-[11px] font-medium leading-tight line-clamp-2" data-testid={`text-search-result-name-${r.id}`}>{r.name}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {searchEnabled && !searchLoading && !searchIsError && (searchData?.hasMore || imgflipPage > 1) && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={imgflipPage <= 1 || searchFetching}
                      onClick={() => setImgflipPage((p) => Math.max(1, p - 1))}
                      data-testid="button-search-prev-page"
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-muted-foreground" data-testid="text-search-page">Page {imgflipPage}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={!searchData?.hasMore || searchFetching}
                      onClick={() => setImgflipPage((p) => p + 1)}
                      data-testid="button-search-next-page"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            ) : templatesLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-xl" />
                ))}
              </div>
            ) : templatesError ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive flex items-center gap-2" data-testid="text-templates-error">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Could not load meme templates from Imgflip. Try again shortly.
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground flex items-center gap-2">
                <ImageIcon className="h-4 w-4 shrink-0" /> No templates match your search.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[480px] overflow-y-auto pr-1">
                {filteredTemplates.map((t) => {
                  const selected = selectedTemplateIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTemplate(t.id)}
                      className={cn(
                        "group relative rounded-xl border overflow-hidden text-left transition-all",
                        selected
                          ? "border-primary ring-2 ring-primary/40"
                          : "border-border/60 hover:border-primary/40 hover:shadow-sm",
                      )}
                      data-testid={`card-template-${t.id}`}
                    >
                      <div className="aspect-square bg-muted/40">
                        <img src={t.url} alt={t.name} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                      {selected && (
                        <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                      <div className="p-1.5">
                        <span className="text-[11px] font-medium leading-tight line-clamp-2" data-testid={`text-template-name-${t.id}`}>{t.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedTemplateIds.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setSelectedTemplateIds([])} data-testid="button-clear-templates">
                <X className="h-3 w-3 mr-1" /> Clear selection
              </Button>
            )}
          </div>

          {/* Aspect ratios — same pill multi-select as Ads Express (no logo
              toggle: memes don't have that concept). Each selected template
              renders once per selected ratio. */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="bar-meme-aspect-ratios">
            <Crop className="h-4 w-4 text-muted-foreground" />
            <span
              className="text-xs font-medium"
              title="Pick one or more. Each meme renders once per selected ratio."
            >
              Aspect ratios
            </span>
            <div
              role="group"
              aria-label="Aspect ratios"
              className="inline-flex items-center rounded-lg border border-border bg-background p-1 shadow-sm"
            >
              {AD_ASPECT_RATIO_OPTIONS.map((opt) => {
                const ratio = opt.value;
                const isActive = aspectRatios.includes(ratio);
                return (
                  <button
                    key={ratio}
                    type="button"
                    role="checkbox"
                    aria-checked={isActive}
                    onClick={() =>
                      setAspectRatios((prev) => {
                        // Toggle, but never allow an empty selection.
                        const next = prev.includes(ratio)
                          ? prev.filter((r) => r !== ratio)
                          : [...prev, ratio];
                        return next.length > 0 ? next : prev;
                      })
                    }
                    disabled={createRun.isPending}
                    className={[
                      "px-3 h-8 rounded-md text-xs font-semibold transition-colors min-w-[52px]",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted",
                      createRun.isPending ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                    ].join(" ")}
                    data-testid={`button-aspect-${ratio.replace(":", "x")}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Generate */}
          <div className="space-y-2 pt-1">
            <div className="text-xs text-muted-foreground" data-testid="text-generate-summary">
              {selectedTemplateIds.length > 0
                ? aspectRatios.length > 1
                  ? `${selectedTemplateIds.length} template${selectedTemplateIds.length === 1 ? "" : "s"} × ${aspectRatios.length} ratios = ${totalMemes} memes`
                  : `${selectedTemplateIds.length} template${selectedTemplateIds.length === 1 ? "" : "s"} selected`
                : "Select one or more templates above"}
            </div>
            {overMemeCap && (
              <p className="text-xs text-destructive" data-testid="text-meme-cap-warning">
                At most 20 memes per run — reduce templates or aspect ratios.
              </p>
            )}
            <Button
              className="w-full"
              disabled={!canGenerate}
              onClick={() => createRun.mutate()}
              data-testid="button-generate"
            >
              {createRun.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Generate {totalMemes > 1 ? `${totalMemes} memes` : "meme"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Active run ── */}
      {/* Only the current run is shown (the one just started, or the latest on
          load) — mirroring Ads Express. Older runs stay in the database and
          their saved memes remain in My Ads. */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold tracking-tight">Your memes</h2>
          {activeRun && (
            <span className="text-xs text-muted-foreground" data-testid="text-active-run-note">
              Showing your latest run — saved memes live in My Ads.
            </span>
          )}
        </div>
        {runsLoading || awaitingNewRun ? (
          <Skeleton className="h-40 rounded-xl" />
        ) : !activeRun ? (
          <div className="rounded-xl border border-dashed border-border/70 p-10 text-center text-sm text-muted-foreground" data-testid="text-no-runs">
            No memes yet. Pick a product or offer, select templates, and hit Generate.
          </div>
        ) : (
          <RunCard
            key={activeRun.id}
            brandId={brandId!}
            run={activeRun}
            entityName={entityNameById.get(activeRun.productId || activeRun.offerId || "") || ""}
            onDeleted={() => setActiveRunId((prev) => (prev === activeRun.id ? null : prev))}
          />
        )}
      </div>
      </div>
    </div>
  );
}
